import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { contributorTasks } from "@/features/contributor-tasks/contributor-tasks";
import { useApp } from "@/hooks/use-app-context";
import { normalizeUserRole } from "@/lib/auth/roles";
import { Icon, type IconName } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { contributorHeaderCopy } from "./header/contributor-header-copy";

const TASK_PATHS = ["/speak-contribute", "/validate-audio", "/transcription", "/transcription-phrase", "/validate-transcription", "/initial-text", "/initial-audio", "/initial-transcription", "/initial-review", "/label-images", "/collector"];

/** Phone navigation of the contributor app: home, tasks, earnings, profile. */
export function MobileBottomNav() {
  const { pathname } = useLocation();
  const { lang = "fr" } = useParams<{ lang: string }>();
  const copy = contributorHeaderCopy[lang === "en" ? "en" : "fr"];
  const base = `/${lang}`;
  const rest = pathname.startsWith(base) ? pathname.slice(base.length) || "/" : pathname;
  const [tasksOpen, setTasksOpen] = useState(false);
  const { user } = useApp();
  const tasks = contributorTasks(lang, { collector: normalizeUserRole(user?.role) === "collector" });
  useEffect(() => setTasksOpen(false), [pathname]);

  const items: Array<{ key: string; label: string; href: string; icon: IconName; active: boolean }> = [
    { key: "home", label: copy.home, href: base, icon: "home", active: rest === "/" || rest === "/dashboard" },
    { key: "tasks", label: copy.tasks, href: `${base}/speak-contribute`, icon: "layoutDashboard", active: TASK_PATHS.some((p) => rest.startsWith(p)) },
    { key: "earnings", label: copy.earnings, href: `${base}/get-paid?section=overview`, icon: "wallet", active: rest.startsWith("/get-paid") || rest.startsWith("/user/contracts") },
    { key: "profile", label: copy.profile, href: `${base}/settings`, icon: "user", active: rest.startsWith("/settings") || rest.startsWith("/my-contributions") },
  ];

  return (
    <nav
      aria-label={copy.nav}
      className="fixed inset-x-3 z-40 flex items-center justify-around rounded-2xl border border-border/60 bg-popover px-1 py-1 md:hidden"
      style={{ bottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}
    >
      {items.map((item) => item.key === "tasks" ? (
        <button
          key={item.key}
          type="button"
          onClick={() => setTasksOpen(true)}
          aria-expanded={tasksOpen}
          className={cn("flex flex-1 flex-col items-center gap-0.5 rounded-xl py-1.5 text-[11px] font-medium", item.active ? "text-foreground" : "text-muted-foreground")}
        >
          <span className={cn("grid size-8 place-items-center rounded-full", item.active && "bg-primary text-primary-foreground")}>
            <Icon name={item.icon} className="size-[18px]" />
          </span>
          {item.label}
        </button>
      ) : (
        <Link
          key={item.key}
          to={item.href}
          aria-current={item.active ? "page" : undefined}
          className={cn("flex flex-1 flex-col items-center gap-0.5 rounded-xl py-1.5 text-[11px] font-medium", item.active ? "text-foreground" : "text-muted-foreground")}
        >
          <span className={cn("grid size-8 place-items-center rounded-full", item.active && "bg-primary text-primary-foreground")}>
            <Icon name={item.icon} className="size-[18px]" />
          </span>
          {item.label}
        </Link>
      ))}
      <Sheet open={tasksOpen} onOpenChange={setTasksOpen}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-2xl border-border/60 px-4 pb-8 pt-5">
          <SheetTitle className="mb-3 text-base font-semibold">{copy.allTasks}</SheetTitle>
          <ul className="grid gap-2">
            {tasks.map((task) => (
              <li key={task.key}>
                <Link to={task.href} className="flex items-center gap-3 rounded-lg border border-border/60 bg-surface p-3">
                  <span className="grid size-9 shrink-0 place-items-center rounded-md bg-brand/10 text-brand">
                    <Icon name={task.icon} className="size-[18px]" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">{task.label}</span>
                    <span className="block truncate text-xs text-muted-foreground">{task.hint}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </SheetContent>
      </Sheet>
    </nav>
  );
}
